import {
  CLEAR_BUYER_DATA,
  CLEAR_IS_GUEST_ORDER, CLEAR_SESSION,
  SET_COUNTRIES,
  SET_SESSION,
  SET_STATES,
  UPDATE_CLIENTS,
  UPDATE_EVENTS,
  UPDATE_PRODUCTS,
  UPDATE_PAYMENT_METHODS,
  UPDATE_SLOTS,
  SET_TITLES, CLEAR_USER, UPDATE_VISITOR_SEGMENTS, UPDATE_EXHIBITOR_DATA, CLEAR_REGISTRATION_CODES, UPDATE_CART,
} from './commits';
import {Client, Country, Event, PaymentMethod, Product, Slot, State, Title, VisitorSegment} from '../Models';
import {
  AuthService,
  ClientService,
  CountryService,
  EventService,
  OrderService,
  ProductService,
  SlotService,
  StateService,
  VisitorSegmentService,
  PaymentMethodService,
} from '../Services';
import {CartDTO, SessionDTO} from '../DTOs';
import {TitleService} from '../Services/TitleService';
import {mergeModelArray} from '../Utilities';

export const actions = {
  async validateSession({commit, state, getters}, dataObject) {
    const authService = new AuthService(dataObject.login);
    const sessionHashDelimiter = '#sesid=';
    const hash = document.location.hash;
    const hashContainsSession = hash.indexOf(sessionHashDelimiter) !== -1;

    if (getters.getSession && getters.getSession.session) {
      if (hashContainsSession) {
        await authService.logout(getters.getSession.session);
        commit(CLEAR_IS_GUEST_ORDER);
        commit(CLEAR_BUYER_DATA);
        commit(CLEAR_USER);
        commit(CLEAR_SESSION);
      } else {
        const sessionValid = await authService.validateSession(getters.getSession.session);
        if (!sessionValid) {
          commit(CLEAR_IS_GUEST_ORDER);
          commit(CLEAR_BUYER_DATA);
          commit(CLEAR_USER);
          commit(CLEAR_SESSION);
        }
      }
    }

    if (hashContainsSession) {
      const sessionBegin = hash.slice(hash.indexOf(sessionHashDelimiter) + sessionHashDelimiter.length);
      const session = sessionBegin.split(';')[0];
      const newHash = window.location.hash.replace(`${sessionHashDelimiter}${session};`, '');

      if (newHash === '#' || newHash === '') {
        history.replaceState({}, document.title, '.');
      }

      const sessionDTO = new SessionDTO();
      sessionDTO.session = session;
      commit(SET_SESSION, sessionDTO);
    }
  },
  async fetchInitialData({commit, state, getters}, {inventoryUrl, cart}: {inventoryUrl: string, cart: CartDTO | null }) {
    let products: Array<Product> = getters.getProducts;
    let slots: Array<Slot> = getters.getSlots;
    let events: Array<Event> = getters.getEvents;
    let clients: Array<Client> = getters.getClients;

    if (cart) {
      // there must not be any registration code available when there is a cart
      // TODO: check what has priority
      commit(CLEAR_REGISTRATION_CODES);
      const productService = new ProductService(inventoryUrl);
      const eventService = new EventService(inventoryUrl);
      const clientService = new ClientService(inventoryUrl);
      const slotService = new SlotService(inventoryUrl);

      const paymentMethodService = new PaymentMethodService(inventoryUrl);
      const paymentMethodsActive = await paymentMethodService.listCached<PaymentMethod>('', {active: 1});
      commit(UPDATE_PAYMENT_METHODS, paymentMethodsActive);

      const productIds: Array<number|string> = [];
      for (const orderItem of cart.order.orderItems) {
        productIds.push(orderItem.productId);
        if (orderItem.subOrderItems) {
          for (const subOrderItem of orderItem.subOrderItems) {
              productIds.push(subOrderItem.productId);
          }
        }
      }
      const foundProductUids = getters.getEvents.map(e => e.id);
      let intersection = productIds.filter(id => foundProductUids.includes(id));
      if (intersection.length !== productIds.length) {
        products = await productService.listCached<Product>('', {id: JSON.stringify(productIds)});
      }

      const slotIds = cart.order.orderItems.filter(oi => oi.slotId !== null).map(oi => oi.slotId);
      if (slotIds.length > 0) {
        slots = await slotService.listCached<Slot>('', {id: JSON.stringify(slotIds)});
      }

      const eventIds = products.map(p => p.eventId);
      const foundEventUids = getters.getEvents.map(e => e.id);
      intersection = eventIds.filter(id => foundEventUids.includes(id));
      if (intersection.length !== eventIds.length) {
        events = await eventService.listCached<Event>('', {id: JSON.stringify(eventIds)});

        const clientIds = events.map(e => e.clientId);
        if (clientIds.length > 0) {
          clients = await clientService.listCached<Client>('', {id: JSON.stringify(clientIds)});
        }
      }
      products.sort((p1, p2) => p1.sortOrder > p2.sortOrder ? 1 : -1);

      if (cart.advertised !== null && cart.advertised.length > 0) {
        try {
          let advertisedProducts: Array<Product> = [];
          const rqs: Array<Promise<Array<Product>>> = [];
          const allCodes: Array<string> = cart.advertised.map(a => `${a.code}`);
          const missingCodes: Array<string> = [...new Set(allCodes)];
          missingCodes.forEach(c => rqs.push(productService.list(c)));
          const results = await Promise.all(rqs);
          results.forEach(r => {
            advertisedProducts = advertisedProducts.concat(r);
          });
          products = mergeModelArray(products, advertisedProducts);

          const advertisedEventIds = advertisedProducts.map(p => p.eventId);
          const missingEventIds = advertisedEventIds.filter(eId => !events.find(e => e.id === eId));
          if (missingEventIds.length) {
            const advertisedEvents = await eventService.listCached<Event>('', {id: JSON.stringify(missingEventIds)});
            events = mergeModelArray(events, advertisedEvents);

            const advertisedClientIds = advertisedEvents.map(e => e.clientId);
            const missingClientIds = advertisedClientIds.filter(eId => !events.find(e => e.id === eId));
            if (missingClientIds.length > 0) {
              const advertisedClients = await clientService.listCached<Client>('', {id: JSON.stringify(missingClientIds)});
              clients = mergeModelArray(clients, advertisedClients);
            }
          }

          products.sort((p1, p2) => p1.sortOrder > p2.sortOrder ? 1 : -1);
        } catch (e) {
          console.error(e);
          cart.advertised = [];
          commit(UPDATE_CART, cart);
        }
      }
    }

    const visitorSegmentService = new VisitorSegmentService(inventoryUrl);
    const visitorSegments = await visitorSegmentService.listCached<VisitorSegment>('', {});
    commit(UPDATE_VISITOR_SEGMENTS, visitorSegments);

    if (state.registrationCodes) {
      const orderService = new OrderService(inventoryUrl);
      try {
        const relatedData = await orderService.getRegistrationRelatedData(state.registrationCodes);

        products = mergeModelArray(products, relatedData.products);
        events = mergeModelArray(events, relatedData.events);
        clients = mergeModelArray(clients, relatedData.clients);
        slots = mergeModelArray(slots, relatedData.slots);

        commit(UPDATE_EXHIBITOR_DATA, relatedData.exhibitorData);
      } catch (e) {
        // Remove registration-code from local storage if code is not valid or expired
        let error: Error = e;
        let errorJson = JSON.parse(error.message);
        if (errorJson.error.error === '1026') {
          commit(CLEAR_REGISTRATION_CODES);
        }
        console.error(e);
      }
    }

    commit(UPDATE_PRODUCTS, products);
    commit(UPDATE_SLOTS, slots);
    commit(UPDATE_EVENTS, events);
    commit(UPDATE_CLIENTS, clients);
  },
  async fetchMiscData({commit, state}, inventoryUrl) {
    if (state.countries.length === 0 || Object.keys(state.states).length === 0) {
      const isDefaultLang = state.language === 'en';
      const countryService = new CountryService(inventoryUrl);
      const titleService = new TitleService(inventoryUrl);
      const stateService = new StateService(inventoryUrl);

      const titles = await titleService.listCached<Title>();
      const countries = await countryService.listCached<Country>();
      if (!isDefaultLang) {
        countries.sort((c1, c2) => {
          const translation1 = c1.translations ? c1.translations[state.language] : null;
          const translation2 = c2.translations ? c2.translations[state.language] : null;

          return translation1 && translation2 ? translation1.title.localeCompare(translation2.title) : -1;
        });
      }
      const requests: Array<Promise<State[]>> = [];
      countries.forEach((c) =>  {
        // Fetch only states for countries where we need it
        if (c.iso31661alpha2 === 'de') {
          requests.push(stateService.listCached<State>(c.id.toString(10)));
        }
      });
      const results = await Promise.all<State[]>(requests);
      const mapping: { [key: number]: Array<State> } = {};
      results.forEach(states => {
        if (states.length > 0) {
          if (!isDefaultLang) {
            states.sort((s1, s2) => {
              const translation1 = s1.translations ? s1.translations[state.language] : null;
              const translation2 = s2.translations ? s2.translations[state.language] : null;

              return translation1 && translation2 ? translation1.title.localeCompare(translation2.title) : -1;
            });
          }
          mapping[states[0].countryId] = states;
        }
      });

      commit(SET_COUNTRIES, countries);
      commit(SET_STATES, mapping);
      commit(SET_TITLES, titles);
    }
  },
};
